// Photo Selection tab component with enhanced A4 multi-page formatting
import React, { useState, useEffect } from 'react';
import { useReports } from '../ReportsProvider';
import { A4Page, useA4Pagination } from '../components/A4Page';

const PhotoSelectionTab = () => {
  const { 
    selectedPeriod,
    selectedSite,
    sites,
    importedPhotos,
    removeImportedPhoto,
    reorderImportedPhotos
  } = useReports();

  // State for photo descriptions and drag-and-drop
  const [photoDescriptions, setPhotoDescriptions] = useState({});
  const [draggedPhotoId, setDraggedPhotoId] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Calculate photos per page - targeting 6 photos per page in 2x3 layout
  // Each photo card is 320px tall for 2x3 grid
  const photosPerPage = 6; // Fixed at 6 photos per page for 2x3 layout

  // Initialize descriptions from photo data
  useEffect(() => {
    const descriptions = {};
    importedPhotos.forEach(photo => {
      if (!photoDescriptions[photo.id]) {
        descriptions[photo.id] = photo.description || '';
      }
    });
    setPhotoDescriptions(prev => ({ ...prev, ...descriptions }));
  }, [importedPhotos]);

  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // Handle photo description changes with character limit for fixed box width
  const handleDescriptionChange = (photoId, description) => {
    // Limit to ~25 characters to fit within 84mm width with 11px font and padding
    // This ensures text doesn't overflow the fixed-width text box
    const truncatedDescription = description.slice(0, 25);
    setPhotoDescriptions(prev => ({
      ...prev,
      [photoId]: truncatedDescription
    }));
  };

  // Remove photo from current selection
  const handleRemovePhoto = (photoId) => {
    removeImportedPhoto(photoId);
    setPhotoDescriptions(prev => {
      const updated = { ...prev };
      delete updated[photoId];
      return updated;
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e, photoId, index) => {
    setDraggedPhotoId(photoId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', photoId);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (draggedPhotoId) {
      const draggedIndex = importedPhotos.findIndex(photo => photo.id === draggedPhotoId);
      
      if (draggedIndex !== dropIndex && draggedIndex !== -1) {
        const reorderedPhotos = [...importedPhotos];
        const [draggedPhoto] = reorderedPhotos.splice(draggedIndex, 1);
        reorderedPhotos.splice(dropIndex, 0, draggedPhoto);
        reorderImportedPhotos(reorderedPhotos);
      }
    }
    
    setDraggedPhotoId(null);
  };

  const handleDragEnd = () => {
    setDraggedPhotoId(null);
    setDragOverIndex(null);
  };

  // Create page header component - fixed dimensions
  const createPageHeader = (pageNumber, totalPages, startIndex, endIndex) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #1d4ed8', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>📸 1.0 Progress Photos</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
            <span style={{ 
              fontSize: '10px', 
              backgroundColor: '#eff6ff', 
              color: '#1e3a8a', 
              padding: '1mm 2mm', 
              borderRadius: '10px' 
            }}>
              Photos {startIndex + 1} - {Math.min(endIndex, importedPhotos.length)} of {importedPhotos.length}
            </span>
          </div>
        </div>
        
        {/* Period and Site Information - only on first page, compact horizontal layout */}
        {pageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm', // Reduced padding for more compact header
            backgroundColor: '#eff6ff', 
            borderRadius: '4px', 
            border: '1px solid #bfdbfe' 
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '3mm', 
              fontSize: '13px', // Increased to match generated date field
              flexWrap: 'wrap' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2mm' }}>
                <span style={{ fontWeight: '600', color: '#1e3a8a' }}>Site:</span>
                <span style={{ color: '#111827' }}>
                  {selectedSite ? sites.find(site => site.id === selectedSite)?.site_name || 'Unknown Site' : 'All Sites'}
                </span>
              </div>
              
              <span style={{ color: '#1d4ed8', fontWeight: 'bold' }}>|</span>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '2mm' }}>
                <span style={{ fontWeight: '600', color: '#1e3a8a' }}>Period:</span>
                <span style={{ color: '#111827' }}>
                  {formatDateToDDMMYYYY(selectedPeriod.startDate) || 'Not Set'} - {formatDateToDDMMYYYY(selectedPeriod.endDate) || 'Not Set'}
                </span>
              </div>
            </div>
            
            {(!selectedPeriod.startDate || !selectedPeriod.endDate) && (
              <div style={{ 
                backgroundColor: '#fef3c7', 
                border: '1px solid #fbbf24', 
                borderRadius: '2px', 
                padding: '2mm', 
                marginTop: '2mm' 
              }}>
                <p style={{ color: '#92400e', fontSize: '10px', margin: 0 }}>
                  ⚠️ Set date range in Cover Page tab, then load photos using buttons above
                </p>
              </div>
            )}
          </div>
        )}
        

      </div>
    </div>
  );

  // Create individual photo card
  const createPhotoCard = (photo, index) => (
    <div
      key={photo.id}
      draggable
      onDragStart={(e) => handleDragStart(e, photo.id, index)}
      onDragOver={(e) => handleDragOver(e, index)}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDrop(e, index)}
      onDragEnd={handleDragEnd}
      title="Drag to reorder photos"
      className={`bg-white rounded-xl border-2 transition-all duration-200 cursor-move border-gray-200 hover:border-blue-300 hover:shadow-md flex flex-col group ${
        draggedPhotoId === photo.id ? 'opacity-50 transform rotate-1' : ''
      } ${
        dragOverIndex === index ? 'transform scale-105 border-blue-400' : ''
      }`}
      style={{
        width: '90mm', // Fixed width for photo cards (wider for 190mm content area)
        height: '80mm', // Reduced height for photo cards (was 110mm)
      }}
    >
      {/* Photo Container - Fixed height */}
      <div className="relative overflow-hidden rounded-t-xl photo-container" style={{
        height: '65mm', // Reduced height for photo area (was 85mm)
        width: '90mm'
      }}>
        <img
          className="photo-card"
          src={photo.url || photo.gcs_url}
          alt={`Progress photo ${photo.name || photo.id}`}
          style={{
            width: '89mm', // Adjusted width to properly fill container without overlap
            height: '65mm', // Match container height
            objectFit: 'cover',
            borderTopLeftRadius: '12px', // Match container's rounded-xl
            borderTopRightRadius: '12px' // Match container's rounded-xl
          }}
        />
        
        {/* Photo number indicator */}
        <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs font-bold">
          #{index + 1}
        </div>
        
        {/* Delete button - only visible on hover */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRemovePhoto(photo.id);
          }}
          className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-red-600 transition-colors shadow-lg opacity-0 group-hover:opacity-100"
          title="Remove photo from report"
        >
          ×
        </button>
      </div>
      
      {/* Description field - Reduced height */}
      <div style={{
        height: '15mm', // Significantly reduced height (was 25mm)
        width: '90mm',
        padding: '1mm 3mm', // Reduced top/bottom padding (was 3mm)
        display: 'flex',
        alignItems: 'center'
      }}>
        <input
          type="text"
          value={photoDescriptions[photo.id] || ''}
          onChange={(e) => handleDescriptionChange(photo.id, e.target.value)}
          placeholder="Add a description for this photo..."
          style={{
            width: '84mm', // Full width minus padding
            height: '12mm', // Reduced height for text box (was 18mm)
            padding: '1mm 3mm', // Reduced padding (was 2mm 3mm)
            fontSize: '11px', // Slightly smaller font (was 12px)
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            outline: 'none',
            overflow: 'hidden', // Prevent text overflow
            textOverflow: 'ellipsis', // Add ellipsis for overflow
            whiteSpace: 'nowrap' // Prevent text wrapping
          }}
          maxLength={25} // Reduced from 40 to fit within box width
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );

  // Create empty state page - fixed dimensions
  const createEmptyStatePage = () => {
    return (
    <A4Page pageNumber={1}>
      <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
        {createPageHeader(1, 1, 0, 0)}
        
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <div style={{ textAlign: 'center' }}>
            <svg style={{ 
              width: '20mm', 
              height: '20mm', 
              margin: '0 auto 6mm auto', 
              color: '#d1d5db', 
              display: 'block' 
            }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#7c3aed', 
              marginBottom: '3mm' 
            }}>No Progress Photos Loaded</h3>
            <p style={{ 
              color: '#4b5563', 
              width: '60mm', 
              margin: '0 auto 6mm auto', 
              lineHeight: '1.5',
              fontSize: '12px'
            }}>
              Use the "📷 Load Work Diary Photos" or "📁 Upload Additional Photos" buttons at the top of the page to add photos to your report.
            </p>
            <div style={{ 
              backgroundColor: '#eff6ff', 
              border: '1px solid #bfdbfe', 
              borderRadius: '4px', 
              padding: '4mm', 
              width: '70mm',
              margin: '0 auto' 
            }}>
              <h4 style={{ 
                fontWeight: '600', 
                color: '#1e3a8a', 
                marginBottom: '2mm',
                fontSize: '12px' 
              }}>Quick Tips:</h4>
              <ul style={{ 
                fontSize: '10px', 
                color: '#1e40af', 
                textAlign: 'left', 
                paddingLeft: '4mm',
                margin: 0,
                lineHeight: '1.4'
              }}>
                <li style={{ marginBottom: '1mm' }}>• Photos will automatically organize across multiple A4 pages</li>
                <li style={{ marginBottom: '1mm' }}>• Drag and drop to reorder photos</li>
                <li style={{ marginBottom: '1mm' }}>• Add descriptions to each photo for context</li>
                <li>• Each page maintains consistent A4 formatting</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </A4Page>
    );
  };

  // Split photos into pages and create A4 pages
  const createPhotoPages = () => {
    if (importedPhotos.length === 0) {
      return [createEmptyStatePage()];
    }

    const pages = [];
    const effectivePhotosPerPage = Math.max(1, photosPerPage);
    const localTotalPages = Math.ceil(importedPhotos.length / effectivePhotosPerPage);
    
    for (let i = 0; i < importedPhotos.length; i += effectivePhotosPerPage) {
      const pagePhotos = importedPhotos.slice(i, i + effectivePhotosPerPage);
      const pageNumber = Math.floor(i / effectivePhotosPerPage) + 1;
      
      const page = (
        <A4Page key={`page-${pageNumber}`} pageNumber={pageNumber}>
          <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
            {/* Page Header */}
            <div style={{ height: '25mm', flexShrink: 0 }}> {/* Reduced from 30mm */}
              {createPageHeader(pageNumber, localTotalPages, i, i + effectivePhotosPerPage)}
            </div>
            
            {/* Photos Grid - 2x3 layout with fixed dimensions */}
            <div style={{ 
              flex: 1, 
              paddingBottom: '10mm',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignContent: 'flex-start',
              gap: '5mm'
            }}>
              {pagePhotos.map((photo, photoIndex) => 
                createPhotoCard(photo, i + photoIndex)
              )}
            </div>
          </div>
        </A4Page>
      );
      
      pages.push(page);
    }
    
    return pages;
  };

  const pages = createPhotoPages();

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {/* Render all A4 pages without extra spacing that causes blank pages in PDF */}
      <div>
        {pages.map((page, index) => (
          <div key={index}>
            {page}
          </div>
        ))}
      </div>

    </div>
  );
};

export default PhotoSelectionTab;